import {
  Box,
  Checkbox,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  withStyles,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import * as Sentry from "@sentry/react";
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import DeleteIcon from "../../../../assets/images/trash_torch_red.svg";
import { ErrorMsg } from "../../../../components/AppMessages";
import { DARK_ACCENT, TORCH_RED, WHITE } from "../../../../constants";
import { Order, View } from "../../../../constants/interface/admin/common";
import { ThemeContext } from "../../../../context";
import { getComparator, stableSort } from "../../utils";

interface Props {
  userViews: any[];
  selectedUserView: any;
  setSelectedUserView: Dispatch<SetStateAction<any>>;
  setDeleteDialogOpen: Dispatch<SetStateAction<boolean>>;
  views: View[];
  searchValue: string;
}

// Zebra-stripe color made theme-aware - see UsersTable.tsx's own copy of this same fix for the
// full reasoning (same bug, same pattern, repeated across every Admin table file).
const getStyledTableRow = (oddRowBackground: string) =>
  withStyles(() => ({
    root: {
      "&:nth-of-type(odd)": {
        backgroundColor: oddRowBackground,
      },
      "&:hover": {
        backgroundColor: "rgba(244, 2, 42, 0.15)",
      },
    },
  }))(TableRow);

const DarkStyledTableRow = getStyledTableRow("rgba(225, 225, 225, 0.15)");
const LightStyledTableRow = getStyledTableRow(`${DARK_ACCENT}1a`);

const WhiteBackgroundCheckbox = withStyles(() => ({
  root: {
    color: "black",
    "& .MuiIconButton-label": {
      position: "relative",
      zIndex: 0,
    },
    "&:not($checked) .MuiIconButton-label": {
      color: WHITE,
    },
    "&$checked .MuiIconButton-label": {
      color: TORCH_RED,
    },
    "&:not($checked) .MuiIconButton-label:after": {
      content: '""',
      left: 4,
      top: 4,
      height: 15,
      width: 15,
      position: "absolute",
      backgroundColor: WHITE,
      zIndex: -1,
    },
    "& .MuiIconButton-colorSecondary": {
      color: `${TORCH_RED} !important`,
    },
  },
  checked: {},
}))(Checkbox);

// Defined once at module scope (rather than calling makeStyles({...})() inline on every render)
// - the inline pattern creates a brand new style-sheet hook per render, which under React 18
// crashes on unmount with "Cannot read properties of undefined (reading 'refs')" in
// @material-ui/styles' detach cleanup.
//
// Two separate static hooks (one per theme) rather than one hook with textColor as a dynamic
// "function of props" rule: this table's background now follows the app's light/dark theme
// (.admin_tile uses themed("primary-tile-background")), and a props-based dynamic color here
// intermittently rendered dark-theme text in DARK_ACCENT (a near-black navy that happens to
// exactly equal the dark theme's own background grey) instead of WHITE - both hooks below are
// always called (Rules of Hooks), and the component picks whichever one matches the current
// theme, so there's no dynamic-value JSS behavior involved at all.
const sharedCellStyles = {
  noBorder: {
    border: "none",
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  deleteIcon: {
    width: "1.5rem",
    cursor: "pointer",
    "&:hover": {
      filter: "brightness(0.85)",
    },
  },
} as const;

const useDarkStyles = makeStyles({
  ...sharedCellStyles,
  tableCell: { color: WHITE, border: "none", padding: "10px !important" },
  tableHeadCell: { color: WHITE, border: "none", fontWeight: 600, padding: "10px !important" },
  orderIcon: { color: `${WHITE} !important`, width: "2.5rem", height: "2.5rem" },
  tablePagination: { color: WHITE, width: "auto" },
});

const useLightStyles = makeStyles({
  ...sharedCellStyles,
  tableCell: { color: DARK_ACCENT, border: "none", padding: "10px !important" },
  tableHeadCell: { color: DARK_ACCENT, border: "none", fontWeight: 600, padding: "10px !important" },
  orderIcon: { color: `${DARK_ACCENT} !important`, width: "2.5rem", height: "2.5rem" },
  tablePagination: { color: DARK_ACCENT, width: "auto" },
});

const UserViewsTable = (props: Props) => {
  const { userViews, selectedUserView, setSelectedUserView, setDeleteDialogOpen, searchValue } = props;
  const [rowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("id");

  const handleRequestSort = (event: any, property: any) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const createSortHandler = (property: any) => (event: any) => {
    handleRequestSort(event, property);
  };

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const headCells = [
    { id: "name", numeric: false, disablePadding: false, label: "Name", sortable: true },
    { id: "views", numeric: false, disablePadding: false, label: "Tabs", sortable: true },
  ];

  const { theme } = useContext(ThemeContext);
  const textColor = theme === "dark" ? WHITE : DARK_ACCENT;
  const darkClasses = useDarkStyles();
  const lightClasses = useLightStyles();
  const classes = theme === "dark" ? darkClasses : lightClasses;
  const StyledTableRow = theme === "dark" ? DarkStyledTableRow : LightStyledTableRow;

  useEffect(() => {
    setPage(0);
  }, [searchValue]);

  return (
    <Sentry.ErrorBoundary fallback={<ErrorMsg />}>
      <Box>
        <TableContainer component={Box}>
          <Table className={`${classes.noBorder}`} data-test-id="user-views-table">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCell} style={{ width: "50px" }}></TableCell>
                {headCells.map((headCell, index) => (
                  <TableCell
                    key={index}
                    className={classes.tableHeadCell}
                    align={headCell.numeric ? "right" : "left"}
                    padding={headCell.disablePadding ? "none" : "normal"}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        style={{ color: textColor, fontWeight: 600 }}
                        classes={{
                          icon: classes.orderIcon,
                        }}
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : "asc"}
                        onClick={createSortHandler(headCell.id)}
                        IconComponent={ArrowDropDownIcon}
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <span className={classes.visuallyHidden}>{order === "desc" ? "sorted descending" : "sorted ascending"}</span>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(userViews, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((userView, index) => (
                  <StyledTableRow key={index}>
                    <TableCell className={`${classes.tableCell}`}>
                      <WhiteBackgroundCheckbox
                        checked={selectedUserView?.id == userView.id}
                        onChange={(e) => {
                          e.target.checked ? setSelectedUserView(userView) : setSelectedUserView(undefined);
                        }}
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                      />
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>{userView.name}</TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                      {/* @ts-ignore */}
                      {Array.isArray(userView.views) && userView.views.map((view) => view.view).join(", ")}
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`} align="right">
                      <img
                        src={DeleteIcon}
                        alt="Delete Icon"
                        className={classes.deleteIcon}
                        onClick={() => {
                          setSelectedUserView(userView);
                          setDeleteDialogOpen(true);
                        }}
                      />
                    </TableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          className={classes.tablePagination}
          rowsPerPageOptions={[]}
          component="div"
          count={userViews.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          backIconButtonProps={{
            style: {
              color: textColor,
            },
          }}
          nextIconButtonProps={{
            style: {
              color: textColor,
            },
          }}
          labelDisplayedRows={({ from, to, count }) => `Showing ${from} to ${to} of ${count} entries`}
        />
      </Box>
    </Sentry.ErrorBoundary>
  );
};

export default UserViewsTable;
